import React from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { COLORS, COLORS_DARK } from '../../constants';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import CustomLegend from '../CustomLegend';
import ExplanationCard from '../ExplanationCard';

const ScatterChartView = ({ selectedMethod, selectedModel, chartData, selectedLanguages }) => {
  const { isDarkMode } = useTheme();
  const colors = isDarkMode ? COLORS_DARK : COLORS;
  const { chartHeight, chartMargin, axisLabelFontSize, tickFontSize } = useResponsiveChart();

  // Convert data to scatter plot format with individual points for each language
  const scatterData = [];
  chartData.forEach((item) => {
    selectedLanguages.forEach(lang => {
      if (item[lang] !== null && item[lang] !== undefined) {
        scatterData.push({
          docCount: item.documents,
          performance: item[lang],
          language: lang,
          color: colors[lang]
        });
      }
    });
  });

  return (
    <div className="mb-8">
      <ExplanationCard title="Scatter Plot Analysis Explanation">
        <p><strong>Scatter Plot:</strong> Shows individual data points for each language and document count combination.</p>
        <p><strong>X-Axis:</strong> Number of documents.</p>
        <p><strong>Y-Axis:</strong> Performance score for that specific configuration.</p>
        <p><strong>Color Coding:</strong> Each language is represented by a different colored dot.</p>
        {/* <p><strong>Interpretation:</strong> Look for clusters, outliers, and patterns. Higher points indicate better performance.</p> */}
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Performance Scatter Analysis
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        Individual data points for {selectedMethod} method - {selectedModel} model.
      </p>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ScatterChart data={scatterData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? '#333333' : '#e0e7ff'} />
            <XAxis
              type="number"
              dataKey="docCount"
              name="Document Count"
              label={{ value: 'Number of Documents', position: 'bottom', offset: 40, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              domain={['dataMin - 1', 'dataMax + 1']}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <YAxis
              type="number"
              dataKey="performance"
              name="Performance"
              label={{ value: 'Performance Score', angle: -90, position: 'insideLeft', offset: 10, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              domain={['dataMin - 10', 'dataMax + 10']}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} wrapperStyle={{ paddingTop: '20px' }} />
            <Scatter name="Performance Data" dataKey="performance" fill="#8884d8">
              {scatterData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ScatterChartView;